import { useState, useEffect } from 'react';
import { Label, Input, Button, fieldClassName, labelClassName, errorTextClassName } from '@shared/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/useAuth';
import { changePassword } from '../../../auth/services/authService';
import { showToast } from '../../../../shared/services/toast';
import { sectionHeading, saveRow, saveButton } from '../profileStyles';

const schema = z.object({
  newPassword: z.string()
    .min(1, 'New password is required.')
    .min(6, 'Password must be at least 6 characters.')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter.')
    .regex(/\d/, 'Password must contain at least one digit.'),
  confirmPassword: z.string()
    .min(1, 'Please confirm your password.')
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match.",
  path: ["confirmPassword"],
});

export function PasswordSection({ onLoadingChange, dataTestId }) {
  const baseTestId = dataTestId || 'profile-password-section';
  const { logout } = useAuth();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading, 'Updating password...');
    }
  }, [isLoading, onLoadingChange]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(schema)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const requestData = {
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      };
      const result = await changePassword(requestData);

      if (result.success) {
        showToast.success(result.message || 'Password changed successfully! You will be logged out for security.');
        reset();
        
        // Logout after 2.5 seconds for security, from every application (NumberCruncher included)
        setTimeout(() => {
          logout({ global: true });
        }, 2500);
      } else {
        showToast.error('Failed to change password.');
      }
    } catch {
      showToast.error('An error occurred while changing password.');
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid={baseTestId}>
      <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Change Password</h3>

      {/* New Password */}
      <div className="space-y-2">
        <Label htmlFor="newPassword" className={labelClassName} data-testid={`${baseTestId}-new-password-label`}>New Password</Label>
        <div className="relative">
          <Input
            id="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            {...register('newPassword')}
            className={`${fieldClassName} pr-10`}
            aria-invalid={!!errors.newPassword}
            data-testid={`${baseTestId}-new-password-input`}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowNewPassword(!showNewPassword)}
            data-testid={`${baseTestId}-new-password-toggle`}
          >
            {showNewPassword ? (
              <EyeOff className="h-4 w-4 text-fg-muted" />
            ) : (
              <Eye className="h-4 w-4 text-fg-muted" />
            )}
          </button>
        </div>
        {errors.newPassword && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-new-password-error`}>{errors.newPassword.message}</p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className={labelClassName} data-testid={`${baseTestId}-confirm-password-label`}>Confirm Password</Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            {...register('confirmPassword')}
            className={`${fieldClassName} pr-10`}
            aria-invalid={!!errors.confirmPassword}
            data-testid={`${baseTestId}-confirm-password-input`}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            data-testid={`${baseTestId}-confirm-password-toggle`}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4 text-fg-muted" />
            ) : (
              <Eye className="h-4 w-4 text-fg-muted" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-confirm-password-error`}>{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className={saveRow} data-testid={`${baseTestId}-actions`}>
        <Button
          type="submit"
          disabled={isSubmitting}
          className={saveButton}
          data-testid={`${baseTestId}-update-button`}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" data-testid={`${baseTestId}-loading-spinner`} />
              Updating...
            </>
          ) : (
            'Update Password'
          )}
        </Button>
      </div>
    </form>
  );
}
